import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Wallet } from "lucide-react";
import { api } from "@/lib/api-client";
import { currency, todayIso } from "@/lib/format";
import { DateInput } from "@/components/ui/DateInput";

type Bucket = { received: number; refunded: number; net: number; count: number };
type CollectionsReportData = {
  from: string;
  to: string;
  totals: { hotel: Bucket; platform: Bucket; all: Bucket };
  byMode: (Bucket & { mode: string; channel: "HOTEL" | "APP" })[];
  byUser: (Bucket & { userId: string | null; name: string })[];
  payments: {
    id: string; paidAt: string; amount: number; status: "SUCCESS" | "REFUNDED"; paymentMode: string; channel: "HOTEL" | "APP";
    transactionRef: string | null; recordedBy: string | null; bookingRef: string; guestName: string;
  }[];
  paymentCount: number;
  // Security deposits (outside the bill) and the desk's cash position with them.
  deposits: { taken: number; takenCount: number; returned: number; usedForBill: number; heldNow: number; heldCount: number };
  hotelCash: number;
};

const MODE_LABELS: Record<string, string> = { CASH: "Cash", UPI: "UPI", CARD: "Card", BANK_TRANSFER: "Bank transfer", OTHER: "Other" };
const modeLabel = (mode: string, channel: "HOTEL" | "APP") => (channel === "APP" ? `App (${MODE_LABELS[mode] ?? mode})` : MODE_LABELS[mode] ?? mode);

// Cash hisaab: money actually collected in a date range - what is with the hotel (taken at the desk) and what is
// with the platform (paid in the guest app, settled to the owner later), by payment mode and by who recorded it.
export function CollectionsReport({ propertyId }: { propertyId: string }) {
  const today = todayIso();
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const valid = Boolean(from && to && from <= to);
  const query = useQuery({
    queryKey: ["bookings", propertyId, "collections", from, to],
    queryFn: () => api<CollectionsReportData>(`/reports/collections?propertyId=${propertyId}&from=${from}&to=${to}`),
    enabled: valid,
  });
  const data = query.data;

  return (
    <section className="card reports-card collections-report" aria-labelledby="collections-title">
      <div className="card__head"><h2 id="collections-title">Collections (cash hisaab)</h2></div>
      <div className="collections-report__range">
        <label>From<DateInput max={to} value={from} onChange={(event) => setFrom(event.target.value)} /></label>
        <label>To<DateInput min={from} max={today} value={to} onChange={(event) => setTo(event.target.value)} /></label>
      </div>
      {!valid && <div className="form-error">Pick a valid date range.</div>}
      {query.isLoading && <div className="form-hint">Loading collections...</div>}
      {query.error && <div className="form-error">{query.error.message}</div>}
      {data && (
        !data.paymentCount ? <div className="mini-empty"><Wallet /><p>No money collected in this period.</p></div> : (
          <>
            <div className="kpi-strip collections-report__totals">
              <div><small>With the hotel</small><strong className="num">{currency.format(data.totals.hotel.net)}</strong><span className="kpi-strip__note">{currency.format(data.totals.hotel.received)} in · {currency.format(data.totals.hotel.refunded)} refunded</span></div>
              <div><small>With the platform (app)</small><strong className="num">{currency.format(data.totals.platform.net)}</strong><span className="kpi-strip__note">Paid out in settlements</span></div>
              <div><small>Total collected</small><strong className="num">{currency.format(data.totals.all.net)}</strong><span className="kpi-strip__note">{data.paymentCount} payment(s) / refund(s)</span></div>
            </div>
            {(data.deposits.taken > 0 || data.deposits.returned > 0 || data.deposits.heldNow > 0) && (
              <div className="kpi-strip collections-report__totals">
                <div><small>Deposits taken</small><strong className="num">{currency.format(data.deposits.taken)}</strong><span className="kpi-strip__note">{data.deposits.takenCount} deposit(s) · {currency.format(data.deposits.returned)} returned · {currency.format(data.deposits.usedForBill)} used for bills</span></div>
                <div><small>Deposits held now</small><strong className="num">{currency.format(data.deposits.heldNow)}</strong><span className="kpi-strip__note">{data.deposits.heldCount} booking(s) - to return or adjust</span></div>
                <div><small>Desk cash incl. deposits</small><strong className="num">{currency.format(data.hotelCash)}</strong><span className="kpi-strip__note">Payments less refunds, plus deposits taken less returned</span></div>
              </div>
            )}
            <div className="collections-report__tables">
              <CollectionsTable title="By payment mode" rows={data.byMode.map((row) => ({ key: `${row.channel}:${row.mode}`, label: modeLabel(row.mode, row.channel), ...row }))} />
              <CollectionsTable title="By who recorded it" rows={data.byUser.map((row) => ({ key: row.userId ?? row.name, label: row.name, ...row }))} />
            </div>
            <details className="collections-report__list">
              <summary>Payments in this period ({data.paymentCount}{data.paymentCount > data.payments.length ? `, latest ${data.payments.length} shown` : ""})</summary>
              <div className="table-wrap">
                <table className="data-table">
                  <thead><tr><th scope="col">Date</th><th scope="col">Booking</th><th scope="col">Mode</th><th scope="col">Recorded by</th><th scope="col" className="num-col">Amount</th></tr></thead>
                  <tbody>
                    {data.payments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="num">{new Date(payment.paidAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td>
                        <td><strong>{payment.bookingRef}</strong><small>{payment.guestName}</small></td>
                        <td>{modeLabel(payment.paymentMode, payment.channel)}{payment.transactionRef ? <small>{payment.transactionRef}</small> : null}</td>
                        <td>{payment.recordedBy ?? "Not recorded"}</td>
                        <td className="num-col num">{payment.status === "REFUNDED" ? "-" : ""}{currency.format(payment.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </>
        )
      )}
    </section>
  );
}

function CollectionsTable({ title, rows }: { title: string; rows: (Bucket & { key: string; label: string })[] }) {
  return (
    <div className="table-wrap">
      <h3 className="collections-report__subtitle">{title}</h3>
      <table className="data-table">
        <thead><tr><th scope="col">{title.replace(/^By /, "").replace(/^\w/, (c) => c.toUpperCase())}</th><th scope="col" className="num-col">In</th><th scope="col" className="num-col">Refunded</th><th scope="col" className="num-col">Net</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{row.label}</td>
              <td className="num-col num">{currency.format(row.received)}</td>
              <td className="num-col num">{currency.format(row.refunded)}</td>
              <td className="num-col num"><strong>{currency.format(row.net)}</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
